import { useState } from 'react'

import { create } from '@bufbuild/protobuf'
import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { Timestamp } from '@bufbuild/protobuf/wkt'
import type { Member } from '@ebalance/proto/bffuser/v1/user_directory_model_pb'
import { UpdateSubStepMemoRequestSchema } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  Text,
  Textarea,
  VStack,
} from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldLabel,
  FieldRoot,
} from '~/components/common/Field'
import { Modal } from '~/components/common/Modal'
import { SaveCancelFooter } from '~/components/common/Modal/SaveCancelFooter'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { formatDateTimeFull } from '~/models/dateTime'
import { resolveMemberDisplayName } from '~/models/member'
import {
  SUB_STEP_MEMO_MAX_LENGTH,
  memoLength,
  truncateMemo,
} from '~/models/memo'
import { useUpdateSubStepMemo } from '~/pages/WorkflowCaseDetail/hooks/useUpdateSubStepMemo'

type Props = {
  isOpen: boolean
  caseId: string
  bigStepDisplayOrder: number
  subStepDisplayOrder: number
  subStepName: string
  /** undefined = 新規追加 (メモ未記入)。 それ以外は編集対象の既存本文。 */
  initialMemo: string | undefined
  memoBy: string | undefined
  memoAt: Timestamp | undefined
  members: Member[]
  onClose: () => void
}

/**
 * サブステップメモの追加・編集モーダル (design/demand/case-detail.html
 * `#cd-memo-modal` 準拠、spec AC-005b、workflow-substep-memo design)。
 *
 * 「メモを追加」ボタンとメモパネルのクリックが同一モーダルを開く (新規 / 編集で
 * UI を分けない、設計書 §4 UI 整合)。保存は raw (trim 前) の本文をそのまま
 * `UpdateSubStepMemo` に送り、trim → 空なら削除の正規化は backend usecase が行う
 * (AC-07)。ただし新規追加で空のまま保存した場合は無駄な API 呼び出しを避けて
 * 黙って閉じる (既存メモがある場合の空保存は AC-07 の削除経路として送信する)。
 */
export const SubStepMemoDialog = ({
  isOpen,
  caseId,
  bigStepDisplayOrder,
  subStepDisplayOrder,
  subStepName,
  initialMemo,
  memoBy,
  memoAt,
  members,
  onClose,
}: Props): React.ReactElement => {
  const showSuccessToast = useSuccessToast()
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useUpdateSubStepMemo(caseId)
  const [draftMemo, setDraftMemo] = useState(initialMemo ?? '')
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  )

  const isEditing = initialMemo !== undefined
  const writerName = resolveMemberDisplayName(members, memoBy)
  const draftLength = memoLength(draftMemo)

  const handleSave = async (): Promise<void> => {
    setErrorMessage(undefined)
    // 新規追加で空のまま保存 = 何もしない (design: 無駄な API 呼び出し / トーストを
    // 出さない)。既存メモを空にして保存する場合は AC-07 の削除経路として送信する。
    if (draftMemo.trim() === '' && !isEditing) {
      onClose()
      return
    }
    try {
      await mutateAsync(
        create(UpdateSubStepMemoRequestSchema, {
          workflowCaseId: caseId,
          bigStepDisplayOrder,
          subStepDisplayOrder,
          memo: draftMemo,
        }),
      )
      onClose()
      showSuccessToast({
        title: t('page:workflowCaseDetail.subStep.memo.saveToast'),
      })
    } catch {
      setErrorMessage(t('page:workflowCaseDetail.error.memoFailed'))
    }
  }

  const footer = (
    <SaveCancelFooter
      isPending={isPending}
      isSaveDisabled={draftLength > SUB_STEP_MEMO_MAX_LENGTH}
      onCancel={onClose}
      onSave={() => {
        void handleSave()
      }}
      saveLabel={t('page:workflowCaseDetail.subStep.memo.save')}
    />
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      title={t('page:workflowCaseDetail.subStep.memo.dialogTitle')}
    >
      <VStack align="stretch" gap={3}>
        {errorMessage !== undefined && (
          <Alert status="error" title={errorMessage} />
        )}
        {/* 対象ステップ (+ 編集時は最終更新) を **素のテキスト 1 行**で出す
            (2026-09-04 デザイナー指摘、2 段)。

            ## 1. 箱と 2 段組みを外す

            以前は design `.cd-dlg-readonly` を写して「背景 + 角丸の box に
            太字の見出し + 小文字の副文を重ねる」階層書式にしていたが、
            **中身がその書式に見合っていなかった**:

            - 副文「このステップのメモを記入します」は modal タイトル
              「ステップメモ」と同じことを言っているだけ
            - 見出し「ステップ『需要想定作成』」の「ステップ」もタイトルと重複

            残るのは**ステップ名**と**最終更新**の 2 つの事実だけで、これは
            見出し / 副文の階層を持たない同格の属性。
            spec AC-WFW-005b の注記も「メモ画面文言 (『メモ』表記の冗長解消) は
            Des / EN 裁量。文脈で自明な箇所は『メモ』の省略可」としている。

            ## 2. ただしラベルと値は識別できるようにする

            1 行に畳んだ結果「最終更新 dev-esquare-broker 2026/09/04 15:49」が
            地続きになり、どこまでが見出しでどこからが値か読めなくなっていた。
            箱や太字は戻さず、**ラベル = midEmphasis / 値 = highEmphasis の濃淡だけ**
            で分ける。文字は md (12px の sm では値の可読性が落ちる)。

            ステップ名にラベルを付けないのは、modal タイトル「ステップメモ」が
            既にその文脈を与えているため (ここで「対象ステップ」と足すと、上で
            落とした重複が戻る)。区切りは `・` を置かず gap で取る。 */}
        {/* 長いステップ名 + 長い記入者名 (英語 locale 等) で modal 幅を超えるので
            折り返す (2026-09-07 レビュー指摘)。 */}
        <VStack align="stretch" gap="2px">
          {/* 役割 typography は `label` (md/600)。design `.cd-dlg-ctx-step` の
              font-weight 600 と同一で、旧実装もこの役割を当てていた。 */}
          <Text textStyle="label" color="Text.highEmphasis">
            {subStepName}
          </Text>
          {isEditing && (
            <HStack flexWrap="wrap" gap={1} fontSize="sm">
              <Text color="Text.midEmphasis">
                {t('page:workflowCaseDetail.subStep.memo.lastUpdatedLabel')}
              </Text>
              <Text color="Text.highEmphasis">
                {t('page:workflowCaseDetail.subStep.memo.lastUpdatedValue', {
                  by: writerName ?? '-',
                  at:
                    memoAt !== undefined
                      ? formatDateTimeFull(timestampDate(memoAt))
                      : '-',
                })}
              </Text>
            </HStack>
          )}
        </VStack>
        {/* 入力欄の不備表示は **DS の form field 構成**に載せる
            (2026-09-07 デザイナー指摘「textarea のエラーが DS 違反」)。
            以前はカウンタ文字列を赤くするだけで、入力欄には枠も `aria-invalid` も
            付いていなかった。`FieldRoot invalid` が配下の `Textarea` へ
            `aria-invalid` と赤枠を、`FieldErrorText` がメッセージを出す。

            `invalid` は **上限に到達した時点から**立てる (2026-09-07 デザイナー
            指摘)。 以前は超過時だけ立て、 ちょうど上限文字数は warning 色の
            カウンタだけで表していたが、 DS `TextArea` が持つ状態は
            Default / Disabled / **Invalid** の 3 つで warning が無く、
            「赤い注意文字だけで枠は通常」 という中間表現は DS に存在しない。

            [DS差異ではなく仕様の言い切り] 上限到達 (ちょうど 500 文字) は
            **保存はできる** (`isSaveDisabled` は `> SUB_STEP_MEMO_MAX_LENGTH`
            のまま)。 「これ以上打てない」 ことを DS の Invalid 表現で伝えるが、
            入力済みの値そのものは spec AC-WFW-005b の上限内なので保存を止めない。
            超過は「サーバから来た 500 文字超の既存メモを開いた」経路でだけ起き、
            そちらは保存も止まる。 */}
        <FieldRoot invalid={draftLength >= SUB_STEP_MEMO_MAX_LENGTH}>
          <FieldLabel>
            {t('page:workflowCaseDetail.subStep.memo.textareaLabel')}
          </FieldLabel>
          {/* DS `Textarea` を使う (2026-09-04 デザイナー指摘)。同じ画面の
              承認メモ / 完了メモ (`MemoDialog`) と同じく `Textarea` で、
              改行はそのまま値に残す (spec AC-WFW-005b「複数行 (改行可)」、
              slice #7328 / #7054)。 */}
          <Textarea
            aria-label={t('page:workflowCaseDetail.subStep.memo.textareaLabel')}
            onChange={(e) => {
              const value = e.target.value
              setDraftMemo((prev) => {
                if (memoLength(value) <= SUB_STEP_MEMO_MAX_LENGTH) {
                  return value
                }
                // 上限超過時、文字数が増えた（貼り付け・入力）ときだけ切り詰める。
                // 文字数が減る操作（削除・バックスペース）ではそのまま減らせるようにする。
                return memoLength(value) > memoLength(prev)
                  ? truncateMemo(value, SUB_STEP_MEMO_MAX_LENGTH)
                  : value
              })
            }}
            placeholder={t('page:workflowCaseDetail.subStep.memo.placeholder')}
            rows={3}
            value={draftMemo}
          />
          {/* カウンタは件数だけを持つ。 理由の文言 (上限に達した / 超えている) は
              DS の error スロット (`FieldErrorText`) が唯一の置き場所で、
              両方に書くと同じことを 2 回読ませることになる。 */}
          <Text
            color={
              draftLength >= SUB_STEP_MEMO_MAX_LENGTH
                ? 'Text.negativeMid'
                : 'Text.lowEmphasis'
            }
            fontSize="xs"
            textAlign="right"
          >
            {t('page:workflowCaseDetail.subStep.memo.counter', {
              count: draftLength,
              max: SUB_STEP_MEMO_MAX_LENGTH,
            })}
          </Text>
          {/* 到達と超過で文言を変える。 ちょうど上限文字数に「上限を超えて
              います」と出すと事実と食い違う。 */}
          <FieldErrorText>
            {draftLength > SUB_STEP_MEMO_MAX_LENGTH
              ? t('page:workflowCaseDetail.subStep.memo.overLimit', {
                  max: SUB_STEP_MEMO_MAX_LENGTH,
                })
              : t('page:workflowCaseDetail.subStep.memo.counterMax')}
          </FieldErrorText>
        </FieldRoot>
      </VStack>
    </Modal>
  )
}
